// League playoffs: no-bracket / setup / active / completed states, with a
// commissioner strip for generating, starting, and finalizing rounds.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { League, BracketResponse } from '../../types';
import BackLink from '../../components/common/BackLink';
import BracketView from '../../components/league/BracketView';

export default function PlayoffsPage() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { user } = useAuth();
  const id = Number(leagueId);

  const [league, setLeague] = useState<League | null>(null);
  const [data, setData] = useState<BracketResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isCommissioner = !!user && league?.commissioner_id === user.id;

  useEffect(() => {
    (async () => {
      setLoading(true);
      setPageError(null);
      try {
        const [lg, bracketData] = await Promise.all([
          apiService.getLeague(id),
          apiService.getPlayoffBracket(id),
        ]);
        setLeague(lg);
        setData(bracketData);
      } catch (e: any) {
        setPageError(e.message || 'Failed to load playoffs');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const generate = async () => {
    setActionError(null);
    setBusy(true);
    try {
      setData(await apiService.generatePlayoffs(id));
    } catch (e: any) {
      setActionError(e.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    setActionError(null);
    setBusy(true);
    try {
      setData(await apiService.startPlayoffs(id));
    } catch (e: any) {
      setActionError(e.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const finalize = async () => {
    setActionError(null);
    setActionMessage(null);
    setBusy(true);
    try {
      const r = await apiService.finalizePlayoffRound(id);
      setData(r);
      setActionMessage(
        r.bracket.status === 'completed'
          ? `${r.bracket.champion?.team_name ?? 'Your champion'} takes the title!`
          : `Round ${r.bracket.current_round} is set.`
      );
    } catch (e: any) {
      setActionError(e.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="py-16 text-center text-secondary-500">Loading…</p>;

  if (pageError) {
    return (
      <div>
        <BackLink to={`/app/leagues/${id}`}>Back to league</BackLink>
        <p className="mt-6 text-sm text-danger">{pageError}</p>
      </div>
    );
  }

  return (
    <div>
      <BackLink to={`/app/leagues/${id}`}>Back to league</BackLink>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 border-b-2 border-ink-900 pb-4 mt-4 mb-8">
        <div>
          <h1 className="font-display text-3xl uppercase tracking-tight text-ink-900">Playoffs</h1>
          {league && <p className="text-sm text-secondary-500 mt-1">{league.name}</p>}
        </div>
      </div>

      {!data ? (
        <div className="py-12 text-center">
          <p className="text-sm text-secondary-500">The bracket hasn&apos;t been drawn up yet.</p>
          {isCommissioner && (
            <div className="mt-4">
              <button onClick={generate} disabled={busy} className="btn-primary">
                {busy ? 'Generating…' : 'Generate Bracket'}
              </button>
              <p className="text-xs text-secondary-400 mt-2">Top half of the standings, seeded by record.</p>
              {actionError && <p role="alert" className="text-sm text-danger mt-2">{actionError}</p>}
            </div>
          )}
        </div>
      ) : data.bracket.status === 'setup' ? (
        <div>
          <div className="bg-white border border-secondary-200 divide-y divide-secondary-200 mb-6">
            {data.seeds.map((s) => (
              <div key={s.id} className="flex items-center gap-3 px-4 py-3">
                <span className="font-display text-secondary-400 w-6">{s.seed}</span>
                <span className="flex-1 min-w-0 truncate text-sm font-medium text-ink-900">
                  {s.team?.team_name ?? 'TBD'}
                </span>
                <span className="text-xs text-secondary-500 w-16 text-right">
                  {s.regular_season_wins}-{s.regular_season_losses}
                </span>
                <span className="font-display text-sm text-secondary-500 w-16 text-right">
                  {s.regular_season_points_for.toFixed(1)}
                </span>
              </div>
            ))}
          </div>
          {isCommissioner && (
            <div>
              <button onClick={start} disabled={busy} className="btn-primary">
                {busy ? 'Starting…' : 'Start Playoffs'}
              </button>
              {actionError && <p role="alert" className="text-sm text-danger mt-2">{actionError}</p>}
            </div>
          )}
        </div>
      ) : (
        <div>
          {isCommissioner && data.bracket.status === 'active' && (
            <div className="mb-6">
              <button onClick={finalize} disabled={busy} className="btn-primary">
                {busy ? 'Finalizing…' : 'Finalize Round'}
              </button>
              {actionError && <p role="alert" className="text-sm text-danger mt-2">{actionError}</p>}
              {actionMessage && <p className="text-sm text-secondary-500 mt-2">{actionMessage}</p>}
            </div>
          )}
          <BracketView leagueId={id} bracket={data.bracket} seeds={data.seeds} rounds={data.rounds} />
        </div>
      )}
    </div>
  );
}
